import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Typography } from 'antd';
import moment from 'moment-timezone';
import { Spacing } from 'works/legacy/lib/style-helper';
import { DateRangeFilter, NumberRangeFilter } from '@common/components';
import {
  buildOptionsFromData,
  FilteredTable,
} from 'works/components/filtered-table';
import { useLazyQuery, useQuery, useNavigate, useParams } from '@common/hooks';
import Filters from 'works/legacy/lib/filters';
import { PAGINATED_SPEARFISHING_MESSAGES } from '@scheduling/graphql/queries/spearfishing';
import { FilterDropdownProps, SorterResult } from 'antd/lib/table/interface';
import { WorksOrganizationUnitFilterDataQuery } from '@scheduling/graphql';
import { MultiSelectTableFilter } from 'works/components/filtered-table/multi-select-table-filter';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import { Membership } from 'works/graphql/works-memberships';
import { RolesQuery, RolesResponse } from '@clinician-profile/graphql';
import { buildDialogUrl } from '@common/services';
import { ENUM_QUERY } from '@common/graphql';

const { Text } = Typography;

export function ShiftMessages({
  hideColumns,
  shiftGapId,
  emptyText,
  hideShiftDetail = false,
}: {
  hideColumns?: string[];
  shiftGapId?: string | undefined;
  emptyText?: ReactElement;
  hideShiftDetail?: boolean;
}) {
  const LOCALSTORAGE_FILTER_KEY = 'ShiftMessages';
  const PAGE_SIZE = shiftGapId ? 50 : 10;
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();

  const [paginationAttrs, setPaginationAttrs] = useState({
    limit: PAGE_SIZE,
    offset: 0,
  });

  const [filters, setFilters] = useState(() =>
    Filters.getFilters(LOCALSTORAGE_FILTER_KEY)
  );

  const onFilterChange = (newFilters: { [key: string]: unknown }) => {
    setFilters(newFilters);
    Filters.setFilters(LOCALSTORAGE_FILTER_KEY, newFilters);
  };

  const [messagesQuery, { loading, data }] = useLazyQuery(
    PAGINATED_SPEARFISHING_MESSAGES,
    {
      notifyOnNetworkStatusChange: true,
    }
  );

  const { data: messageTypeData } = useQuery(ENUM_QUERY, {
    fetchPolicy: 'cache-first',
    variables: { key: 'SpearfishingMessageTypeDescription' },
  });

  const { data: organizationUnitData } = useQuery<{
    organizationUnits: unknown[];
  }>(WorksOrganizationUnitFilterDataQuery, {
    fetchPolicy: 'cache-first',
    skip: !!shiftGapId,
  });

  const { data: rolesData } = useQuery<RolesResponse>(RolesQuery, {
    skip: !!shiftGapId,
  });

  const messageTypes = messageTypeData?.enum;
  const messageTypeOptions = useMemo(
    () =>
      messageTypes?.map((type) => ({
        text: type.description,
        value: type.name,
      })),
    [messageTypes]
  );

  useEffect(() => {
    messagesQuery({
      variables: {
        ...paginationAttrs,
        filters: shiftGapId
          ? {
              shiftGapId,
            }
          : filters,
      },
    });
  }, [filters, paginationAttrs, messagesQuery, shiftGapId]);

  const messages =
    data?.paginatedSpearfishingCampaigns?.spearfishingCampaigns || [];
  const total = data?.paginatedSpearfishingCampaigns?.totalCount || null;

  const columns = useMemo(
    () =>
      [
        {
          title: 'Sent Date/Time',
          dataIndex: 'createdAt',
          key: 'sentDate',
          render: (
            createdAt: string,
            record: {
              shiftGap: { timeZone: string };
            }
          ) => {
            const startTime = moment
              .parseZone(createdAt)
              .tz(record?.shiftGap?.timeZone);
            const date = startTime?.format('MM/DD/yyyy');
            const time = startTime?.format('HH:mm');
            return (
              <>
                <Text>{date}</Text>
                <br />
                <Text>{time}</Text>
              </>
            );
          },
          sorter: true,
          filterFormat: 'dateRange',
          filtered: filters.createdAt || false,
          filteredValue: filters.createdAt || null,
          filterDropdown: ({
            setSelectedKeys,
            confirm,
          }: FilterDropdownProps) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <DateRangeFilter
                defaultValue={filters.createdAt?.map((date: string) =>
                  moment(date, 'YYYY-MM-DD')
                )}
                autoFocus={true}
                onSearch={(value) => {
                  setSelectedKeys(
                    value
                      ? value.map((date) => moment(date).format?.('YYYY-MM-DD'))
                      : []
                  );
                  confirm();
                }}
              />
            </section>
          ),
        },
        {
          title: 'Type',
          dataIndex: 'messageType',
          key: 'messageType',
          sorter: true,
          filterDropdown: MultiSelectTableFilter,
          render: (messageType: string) => {
            const messageTypeOption = messageTypeOptions?.find(
              (option) => option.value === messageType
            );
            const formattedMessageType = messageType.replace('_', ' ');

            return (
              <Text style={{ textTransform: 'capitalize' }}>
                {messageTypeOption?.text || formattedMessageType}
              </Text>
            );
          },
          filtered: filters.messageType || false,
          filteredValue: filters.messageType || null,
          filters: messageTypeOptions,
        },
        {
          title: 'Recipients',
          dataIndex: 'spearfishingNotifications',
          key: 'spearfishingNotifications',
          sorter: true,
          render: (spearfishingNotifications: { id: string }[]) => {
            return (
              <Text style={{ textTransform: 'capitalize' }}>
                {spearfishingNotifications.length || 0}
              </Text>
            );
          },
        },
        {
          title: 'Shift Date/Time',
          dataIndex: 'shiftGap',
          key: 'shiftStartDate',
          render: (shiftGap: { startDatetime: string; timeZone: string }) => {
            const startTime = moment
              .parseZone(shiftGap?.startDatetime)
              .tz(shiftGap?.timeZone);
            const date = startTime?.format('MM/DD/yyyy');
            const time = startTime?.format('HH:mm');
            return (
              <>
                <Text>{date}</Text>
                <br />
                <Text>{time}</Text>
              </>
            );
          },
          sorter: true,
          filterFormat: 'dateRange',
          filtered: filters.shiftStartDate || false,
          filteredValue: filters.shiftStartDate || null,
          filterDropdown: ({
            setSelectedKeys,
            confirm,
          }: FilterDropdownProps) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <DateRangeFilter
                defaultValue={filters.createdAt?.map((date: string) =>
                  moment(date, 'YYYY-MM-DD')
                )}
                autoFocus={true}
                onSearch={(value) => {
                  setSelectedKeys(
                    value
                      ? value.map((date) => moment(date).format?.('YYYY-MM-DD'))
                      : []
                  );
                  confirm();
                }}
              />
            </section>
          ),
        },
        {
          title: 'Duration',
          dataIndex: 'shiftGap',
          key: 'duration',
          render: (shiftGap: { shiftDuration: string }) =>
            `${shiftGap.shiftDuration} hrs`,
          sorter: true,
          filterFormat: 'hoursRange',
          filtered: filters.duration || false,
          filteredValue: filters.duration || null,
          filterDropdown: ({
            setSelectedKeys,
            confirm,
          }: FilterDropdownProps) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <NumberRangeFilter
                onSearch={(value) => {
                  setSelectedKeys(value || []);
                  confirm();
                }}
                min={1}
                max={24}
                defaultValue={filters.duration}
                enforceOrder
              />
            </section>
          ),
        },
        {
          title: 'Unit / Cost Center',
          dataIndex: 'shiftGap',
          key: 'unit',
          render: (shiftGap: {
            organizationUnit: { name: string; costCenter: string };
          }) => (
            <>
              <Text>{shiftGap.organizationUnit.name}</Text>
              <br />
              <Text type="secondary">
                {shiftGap.organizationUnit.costCenter}
              </Text>
            </>
          ),
          sorter: true,
          filtered: filters.unit || false,
          filteredValue: filters.unit || null,
          filters: buildOptionsFromData(
            organizationUnitData?.organizationUnits || [],
            (unit: { name: string; costCenter: string; id: string }) => ({
              text: `${unit.name} - ${unit.costCenter}`,
              value: unit.id,
            })
          ),
          filterDropdown: MultiSelectTableFilter,
        },
        {
          title: 'Role',
          dataIndex: 'shiftGap',
          key: 'role',
          sorter: true,
          filtered: filters.role || false,
          filteredValue: filters.role || null,
          filters: buildOptionsFromData(
            rolesData?.roles || [],
            (role: { id: string; abbreviation: string } | undefined) => ({
              text: role?.abbreviation,
              value: role?.id,
            })
          ),
          filterDropdown: MultiSelectTableFilter,
          render: (shiftGap: { role?: { abbreviation: string } }) => (
            <>{shiftGap.role?.abbreviation}</>
          ),
        },
        {
          title: 'Sender',
          dataIndex: 'createdBy',
          key: 'sender',
          sorter: true,
          filtered: filters.sender || false,
          filteredValue: filters.sender || null,
          filterDropdown: TextSearchTableFilter,
          render: (sender: { worksUser: { name: string } }) => (
            <>{sender.worksUser.name}</>
          ),
          className: 'sentry-mask',
        },
      ].filter((column) => !hideColumns?.includes?.(column.key as string)),
    [
      filters.createdAt,
      filters.duration,
      filters.messageType,
      filters.role,
      filters.sender,
      filters.shiftStartDate,
      filters.unit,
      hideColumns,
      messageTypeOptions,
      organizationUnitData?.organizationUnits,
      rolesData?.roles,
    ]
  );

  const handleTableChange = (
    pagination: { current: number; pageSize: number },
    newFilters: { [key: string]: unknown },
    sorter: SorterResult<Membership>
  ) => {
    const pageSize = pagination.pageSize || PAGE_SIZE;
    const newPaginationAttrs = {
      offset: (pagination.current - 1) * pageSize,
      limit: pageSize,
      orderField: 'SHIFT_START_DATE',
      orderDirection: 'DESC',
    };

    if (sorter.field) {
      const attributeDict = {
        role: 'ROLE',
        messageType: 'MESSAGE_TYPE',
        duration: 'DURATION',
        sentDate: 'SENT_DATE',
        shiftStartDate: 'SHIFT_START_DATE',
        unit: 'UNIT',
        sender: 'SENDER',
        spearfishingNotifications: 'RECIPIENTS',
      };

      newPaginationAttrs['orderField'] =
        attributeDict[sorter?.columnKey as keyof typeof attributeDict];
    }

    if (sorter?.order) {
      newPaginationAttrs.orderDirection =
        sorter.order === 'ascend' ? 'ASC' : 'DESC';
    }

    setPaginationAttrs(newPaginationAttrs);
    onFilterChange(newFilters);
  };

  const buildMessageDetailDrawerUrl = (id: string) =>
    buildDialogUrl('shift-message-detail-drawer', {
      props: {
        from: 'shiftDetails',
        hideShiftDetail,
        messageId: id,
        type: type || '',
      },
    });

  return (
    <>
      <FilteredTable
        className="ShiftMessages"
        columns={columns}
        rowKey="id"
        loading={loading}
        dataSource={messages}
        pagination={{
          ...paginationAttrs,
          total,
          position: shiftGapId ? ['none', 'none'] : undefined,
          ...(shiftGapId ? { pageSize: PAGE_SIZE } : {}),
        }}
        onFilterChange={onFilterChange}
        filters={filters}
        localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
        onChange={handleTableChange}
        scroll={shiftGapId ? { x: '100%' } : undefined}
        locale={emptyText ? { emptyText: emptyText } : undefined}
        onRow={(record: { id: string }) => {
          return {
            onClick: () => {
              navigate(buildMessageDetailDrawerUrl(record.id));
            },
          };
        }}
      />
    </>
  );
}
